import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useNotifier } from '@provider/notifier';
import { useUserIdentityState } from '@provider/user-identity';
import { getAllCoursesQuery } from './query.get-all.js';
import { deleteCourseRequest } from './request.delete.js';

function useDeleteCourseMutation() {
  const client = useQueryClient();
  const notifier = useNotifier();
  const user = useUserIdentityState(state => state.user);
  return useMutation(deleteCourseMutation(client, notifier, user));
}

/**
 * Deletes a course and removes it from the internal store collection
 * optimistically.
 */
const deleteCourseMutation = (client, notifier, user) => {
  return {
    mutationKey: ["course-builder/delete", user?.id],
    mutationFn: course => deleteCourseRequest(course),
    onMutate: async course => {
      await client.cancelQueries({
        queryKey: getAllCoursesQuery.queryKey({
          user
        })
      });
      const data = client.getQueryData(getAllCoursesQuery.queryKey({
        user
      }));
      const deletedCourseIndex = data?.findIndex(it => it.id === course.id);
      client.setQueryData(getAllCoursesQuery.queryKey({
        user
      }), previous => previous?.filter(it => it.id !== course.id));
      return {
        deletedCourse: course,
        index: deletedCourseIndex !== -1 ? deletedCourseIndex : undefined
      };
    },
    onSuccess: () => {
      notifier.notify({
        id: "course-builder.delete",
        type: "success",
        body: "Forløbet er blevet slettet."
      });
    },
    onError: (error, _, context) => {
      notifier.notify({
        id: "course-builder.delete.error",
        type: "error",
        body: "Forløbet kunne ikke slettes. Prøv igen om lidt."
      });
      client.setQueryData(getAllCoursesQuery.queryKey({
        user
      }), previous => {
        if (!context) {
          return previous;
        }
        const next = [...(previous ?? [])];
        if (context?.index) {
          next.splice(context.index, 0, context.deletedCourse);
        } else {
          next.push(context.deletedCourse);
        }
        return next;
      });
    }
  };
};

export { useDeleteCourseMutation };
//# sourceMappingURL=mutation.delete.js.map
